import { DailyInsightQueryResult, DailyInstagramInsight } from '~/generated/graphql'
import { CompanyInstagramInsgihtChartData } from '~/components/common/InstagramInsight/CompanyInstagramInsightChart'
import { DateTime } from 'luxon'

export type ProfileViewGrowthRateResult = {
  growthRate: number | null
  /** 最初の値が0でgrowthRateを算出できないとき true（UIで「—」表示） */
  unavailable: boolean
}

/**
 * 日次インサイトからプロフィール閲覧数の成長率を算出する
 */
export const calcProfileViewGrowthRateFromDailyInsight = (
  data: readonly DailyInstagramInsight[],
): ProfileViewGrowthRateResult => {
  if (data.length <= 1) {
    return { growthRate: null, unavailable: false }
  }

  const sorted = [...data].sort(
    (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
  )
  const oldest = sorted[0]!
  const newest = sorted[sorted.length - 1]!

  if (oldest.profileViewCount === 0) {
    return { growthRate: null, unavailable: true }
  }

  return {
    growthRate: calcCompanyInsightGrowthRate(newest.profileViewCount, oldest.profileViewCount),
    unavailable: false,
  }
}

/**
 * ホーム画面のプロフィール閲覧数グラフ・ヒント表示用
 */
export const calcHomeProfileViewGrowthRate = (
  data: DailyInsightQueryResult['data'],
): {
  graphData: CompanyInstagramInsgihtChartData | null
  profileViewGrowthRate: number | null
  profileViewGrowthRateUnavailable: boolean
  thisWeekFollowerCount: number
  profileViewCountSum: number | null
} => {
  const snsConnections = data?.viewer.company?.snsConnections
  const currentAccountInsight = snsConnections?.[0]?.dailyInstagramInsight
  if (!currentAccountInsight) {
    return {
      graphData: null,
      profileViewGrowthRate: null,
      profileViewGrowthRateUnavailable: true,
      thisWeekFollowerCount: 0,
      profileViewCountSum: null,
    }
  }

  const graphData: CompanyInstagramInsgihtChartData = currentAccountInsight.map((x) => ({
    date: DateTime.fromISO(x.date).toFormat('yyyy/MM/dd'),
    value: x.profileViewCount,
    followerCount: x.followerCount,
  }))

  const { growthRate, unavailable } = calcProfileViewGrowthRateFromDailyInsight(currentAccountInsight)

  const profileViewCountSum = currentAccountInsight.reduce((prev, cur) => prev + cur.profileViewCount, 0)

  const thisWeekFollowerCount = currentAccountInsight
    .map(({ followerCount }) => followerCount)
    .reduce((prev, cur) => prev + cur, 0)

  return {
    graphData,
    profileViewGrowthRate: growthRate,
    profileViewGrowthRateUnavailable: unavailable,
    thisWeekFollowerCount,
    profileViewCountSum,
  }
}

/**
 * 成長率の計算関数
 */
export const calcCompanyInsightGrowthRate = (newVal: number, oldVal: number) => {
  if (!oldVal) return 0
  return Math.round(((newVal - oldVal) / oldVal) * 100)
}

/**
 * 成長率の計算関数のラッパー。入力値のいずれかがnullの場合はnullを返す
 */
export const calcGrowthRateIfNotNull = (newValue: number | null, oldValue: number | null): number | null => {
  if (newValue === null || oldValue === null) {
    return null
  }
  return calcCompanyInsightGrowthRate(newValue, oldValue)
}

/**
 * 取得したDailyInstagramInsiht配列データをもとに、グラフ表示用のデータを作成する
 * @param data
 * @param key 指定したキーの値をグラフの値として取得し、投稿数も付加する
 * @param maxLength 指定した場合は、指定した数分のグラフ用データを作成する
 */
export const convertDailyInsightToChartData = (
  data: readonly DailyInstagramInsight[] | null | undefined,
  key: 'profileViewCount' | 'reachCount' | 'followerCount' | 'siteClickCount',
  maxLength?: number,
) => {
  if (!data) return []
  const sliced = maxLength ? data.slice(-maxLength) : data
  return sliced.map((d) => ({
    date: DateTime.fromISO(d.date).toFormat('yyyy/MM/dd'),
    value: d[key],
    followerCount: d.followerCount,
  }))
}

/**
 * 取得したDailyInstagramInsiht配列データをもとに合計値を計算する
 * @param data
 * @param key
 * @param start 開始位置
 * @param length 集計対象データ数（日数）
 */
export const getSumOfDailyInsight = (
  data: readonly DailyInstagramInsight[] | null | undefined,
  key: 'profileViewCount' | 'reachCount' | 'siteClickCount',
  start: number,
  length: number,
) => {
  if (!data) return null
  const sliced = length ? data.slice(start, start + length) : data
  return sliced.reduce((acc, d) => acc + d[key], 0)
}
